import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LotSaleStatusLabel } from "src/components/atoms/LotSaleStatusLabel";
import { useViewport } from "src/contexts";
import {
  CommunityOrLotStepper,
  ConfiguratorMap,
  scrollToStepWrapperElement,
  SkeletonStepper,
  StepWrapper,
} from "src/pages/configurator/components";
import { createCommunitiesUrl, createConfiguratorUrl, createPlanSelectionUrl } from "src/RouteUrls";
import { LotMap, LotMapSalesStatus, MapMarker, SelectableOptionCard } from "~components";
import { Css, Palette } from "~generated/css";
import { LotSalesStatus } from "~generated/graphqlTypes";
import {
  currencyFormatter,
  getPlanBathroomRangeString,
  getPlanBedroomRangeString,
  getPlanSqftRangeString,
  getProgramDataStrings,
  queryResult,
  removePilotPrefix,
  useTestIds,
} from "~utils";
import { useConfiguratorContext } from "../Configurator.context";
import { structureIdToLastConfigId } from "../structureConfigMap";
import { getCommunityProgramDataRange } from "./Communities.step";
import { FlowStep } from "./FlowSteps";
import {
  LotStep_BuildablePlansFragment,
  LotStep_Community_ProductOfferingFragment,
  LotStep_CommunityFragment,
  LotStep_StructureFragment,
  useLotStepQuery,
} from "./Lot.step.generated";

// NOTE: While we call this the `Lot selection` step, users are actually selecting a `Structure`. `Structures` are what is attached to CheckoutConfigs
export function LotStep() {
  const { communityId } = useParams();
  if (!communityId) {
    throw new Error("Community ID is required for LotStep");
  }
  const query = useLotStepQuery({ variables: { communityId } });
  return queryResult(query, {
    data: (data) => {
      // Only include program data for product offerings that are checkout enabled.
      const filteredProductOfferings = data.community.productOfferings.filter((po) => po.showInCheckout);

      const communityWithFilteredOfferings = {
        ...data.community,
        productOfferings: filteredProductOfferings,
      };

      return <LotStepView community={communityWithFilteredOfferings} lots={data.structures.entities} />;
    },
    loading: () => {
      return (
        <>
          <StepWrapper
            title="Lot"
            loading
            description="Select a lot from this community"
            left={<ConfiguratorMap center={"continentalUS"} />}
            right={<></>}
          />
          <SkeletonStepper showBack />
        </>
      );
    },
  });
}

function LotStepView(props: { community: LotStep_CommunityFragment; lots: LotStep_StructureFragment[] }) {
  const { community, lots } = props;
  const navigate = useNavigate();
  const { isMobile } = useViewport();
  const [hoveredLot, setHoveredLot] = useState<string | undefined>();
  const { communityId, lotId, setLotId, autoSaving } = useConfiguratorContext();

  const useLotMap = !!community.lotMap;

  const communityLotMapSaleStatus = useMemo(() => {
    // Don't want to clog error channel with this if we're not using the lot map at all.
    return useLotMap
      ? lots.map((lot) => ({
          id: lot.id,
          state: getLotMapSaleStatus(lot, community),
        }))
      : [];
  }, [lots, community, useLotMap]);

  const mapMarkers = useMemo(() => {
    return lots
      .filter((c) => c.latitude && c.longitude)
      .map((lot) => {
        return {
          id: lot.id,
          center: { lat: Number(lot.latitude), lng: Number(lot.longitude) },
          marker: (
            <MapMarker
              onHoverStart={() => setHoveredLot(lot.id)}
              onHoverEnd={() => setHoveredLot(undefined)}
              onClick={() => {
                setLotId(lot.id);
                scrollToStepWrapperElement(`${lot.id}-card`, isMobile);
              }}
              isActive={lotId === lot.id}
              isHovered={hoveredLot === lot.id}
              data-testid={`mapMarker-${lot.id}`}
            />
          ),
        };
      });
  }, [lots, hoveredLot, lotId, setLotId, isMobile]);

  function onLotSelect(lotId: string) {
    // First, check if this lot/structure already has a checkoutConfig in our map
    const existingConfigFromMap = structureIdToLastConfigId.get(lotId);
    if (existingConfigFromMap) {
      setLotId(lotId);
      return navigate(createConfiguratorUrl(communityId!, existingConfigFromMap, FlowStep.Plans));
    }
    setLotId(lotId);
    return navigate(createPlanSelectionUrl(communityId!, lotId));
  }

  return (
    <>
      <StepWrapper
        title="Lot"
        description="Select a lot from this community"
        showMaxExpand={useLotMap}
        left={
          useLotMap ? (
            <LotMap
              svg={community.lotMap!}
              lots={communityLotMapSaleStatus}
              hoveredLotId={hoveredLot}
              selectedLotId={lotId ?? undefined}
              onClick={(lotId) => {
                setLotId(lotId);
                scrollToStepWrapperElement(`${lotId}-card`, isMobile);
              }}
              onHover={(lotId) => setHoveredLot(lotId ?? undefined)}
            />
          ) : (
            <ConfiguratorMap markers={mapMarkers} center={mapMarkers.length === 0 ? "continentalUS" : undefined} />
          )
        }
        right={lots.map((lot) => (
          <div id={`${lot.id}-card`} key={lot.id}>
            <SelectableOptionCard
              title={removePilotPrefix(lot.name)}
              description={<LotCardDescription community={community} structure={lot} useNewDescription={useLotMap} />}
              selected={lotId === lot.id}
              onClick={() => setLotId(lot.id)}
              onHoverStart={() => setHoveredLot(lot.id)}
              onHoverEnd={() => setHoveredLot(undefined)}
              isHovered={hoveredLot === lot.id}
              disabled={autoSaving || isLotSoldOrReserved(lot.lotSalesStatus?.code)}
            />
          </div>
        ))}
      />
      <CommunityOrLotStepper
        onBack={() => {
          navigate(createCommunitiesUrl());
        }}
        onNext={() => lotId && onLotSelect(lotId)}
        disabledReasons={!lotId ? ["Please select a lot"] : []}
      />
    </>
  );
}

function LotCardDescription(props: {
  community: LotStep_CommunityFragment;
  structure: LotStep_StructureFragment;
  // Updated descriptions depend on the lot map
  // This is a temp flag so we dont mess up dallas infill lots while we dont have all the data
  // in yet. Need to backfill lot status and get the `isConstructionComplete` flag in.
  useNewDescription: boolean;
}) {
  const { community, structure, useNewDescription } = props;
  const { readyPlanConfig, hasStartedConstruction, isConstructionComplete } = structure;
  const salesPrice = readyPlanConfig?.includedPriceInCents;
  const tid = useTestIds({}, "lot-card-description");

  // A range of offerings used before construction has begun
  const communityProgramDataRange = getCommunityProgramDataRange(community);
  // What the structure is currently specced to after construction has begun. We use this when construction has completed.
  const structureProgramData = getProgramDataStrings(readyPlanConfig?.programData);

  const salesPriceLabel = salesPrice
    ? isConstructionComplete && useNewDescription
      ? `${currencyFormatter(salesPrice)}`
      : `Starting from ${currencyFormatter(salesPrice)}`
    : undefined;

  function getProgramDataDescription() {
    // When construction has not started yet we show no program data because users can still select a plan
    if (useNewDescription && !hasStartedConstruction) {
      return null;
    }
    return (
      <div css={Css.df.gap1.aic.t14.gray80.$} {...tid.programData}>
        <span>
          {isConstructionComplete
            ? structureProgramData.bedrooms
            : getPlanBedroomRangeString(communityProgramDataRange)}
        </span>
        <span>
          {isConstructionComplete
            ? structureProgramData.bathrooms
            : getPlanBathroomRangeString(communityProgramDataRange)}
        </span>
        <span>
          {isConstructionComplete ? structureProgramData.sqft : getPlanSqftRangeString(communityProgramDataRange)}
        </span>
      </div>
    );
  }
  // If buildablePlans is empty, assume all community plans are buildable
  const buildablePlans = structure.parent?.buildablePlans.nonEmpty
    ? structure.parent?.buildablePlans
    : community.productOfferings;

  return (
    <>
      {useNewDescription ? (
        <LotMapCustomizationLabel
          buildablePlans={buildablePlans}
          planName={structure.readyPlanConfig?.readyPlan?.name}
          lotSalesStatus={getLotMapSaleStatus(structure, community)}
          hasStartedConstruction={hasStartedConstruction}
        />
      ) : (
        <CustomizationLabel
          planName={structure.readyPlanConfig?.readyPlan?.name}
          hasStartedConstruction={hasStartedConstruction}
        />
      )}
      {getProgramDataDescription()}
      {!!salesPriceLabel && <div css={Css.t14.gray80.$}>{salesPriceLabel}</div>}
    </>
  );
}

export function CustomizationLabel(props: { planName?: string; hasStartedConstruction: boolean }) {
  const { planName, hasStartedConstruction } = props;

  // Before we start construction (Ready to Start milestone), they can change the plan
  // After (Ready to Start) they can personalize within the plan
  const color = !hasStartedConstruction ? Palette.BrightGreen : Palette.BrightYellow;
  const cutoffText = !hasStartedConstruction ? "Fully Customizable" : "Customizable";
  const showPlanName = hasStartedConstruction && planName;
  return (
    <div css={Css.df.aic.gap(1.5).t14.$}>
      {showPlanName && <div css={Css.gray80.$}>{planName}</div>}
      <div css={Css.color(color).$}>{cutoffText}</div>
    </div>
  );
}

// TODO: Rename this and other `useLotMap` related components to something more aligned with that use case since this
// both are going to stick around for a while :sob:
function LotMapCustomizationLabel(props: {
  planName?: string;
  lotSalesStatus: LotMapSalesStatus;
  hasStartedConstruction: boolean;
  buildablePlans: LotStep_BuildablePlansFragment[] | LotStep_Community_ProductOfferingFragment[];
}) {
  const { buildablePlans, planName, lotSalesStatus, hasStartedConstruction } = props;
  // Before we start construction (Ready to Start milestone), they can change the plan
  // After (Ready to Start) they can personalize within the plan
  // After (Move-in Ready) they can't change the plan
  const showPlanName = hasStartedConstruction && planName;
  const showBuildablePlans = !hasStartedConstruction;

  return (
    <>
      {showPlanName ? (
        <div css={Css.t14.gray80.$}>{planName}</div>
      ) : (
        showBuildablePlans && (
          <div css={Css.t14.gray80.$}>
            {buildablePlans.length === 1 ? buildablePlans[0].displayName : `Choose from ${buildablePlans.length} Plans`}
          </div>
        )
      )}
      <div css={Css.df.aic.gap1.$}>
        <LotSaleStatusLabel status={lotSalesStatus} isCardLotLabel />
      </div>
    </>
  );
}

function isLotSoldOrReserved(lotSalesStatusCode: LotSalesStatus | null | undefined): boolean {
  return lotSalesStatusCode === LotSalesStatus.Sold || lotSalesStatusCode === LotSalesStatus.Reserved;
}

// Return a LotMapSalesStatus based on precedence within the lot sales status enum and construction state
export function getLotMapSaleStatus(
  lot: LotStep_StructureFragment,
  community: LotStep_CommunityFragment,
): LotMapSalesStatus {
  // Prioritize Model home and reserved statuses. Map treats sold as reserved
  if (lot.lotSalesStatus?.code === LotSalesStatus.ModelHome) {
    return LotMapSalesStatus.ModelHome;
  } else if (lot.lotSalesStatus?.code === LotSalesStatus.Sold || lot.lotSalesStatus?.code === LotSalesStatus.Reserved) {
    return LotMapSalesStatus.Reserved;
  }

  // Then show status based on construction state
  if (lot.isConstructionComplete) {
    return LotMapSalesStatus.MoveInReady;
  } else if (lot.hasStartedConstruction) {
    return LotMapSalesStatus.ConstructionStarted;
  }

  if (lot.lotSalesStatus?.code === LotSalesStatus.Available) {
    return LotMapSalesStatus.Available;
  }

  // If we get here the structure has the `Not Available` lot status and return existing residential as fallback since Not Available
  // is not on the lot map
  // Throw a warning since either our sf sync is broken or we're showing a structure in checkout that we shouldn't be.
  console.error(
    `Community ${community.name} (${community.id}): Structure ${lot.id} has lot sales status ${lot.lotSalesStatus?.code} and is not construction complete or started. This structure likely should not be shown in checkout.`,
  );

  return LotMapSalesStatus.ExistingResidential;
}
